import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Alert,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  CircularProgress,
  useTheme,
  alpha,
  LinearProgress
} from '@mui/material';
import { styled } from '@mui/material/styles';
import {
  CheckCircle as CheckIcon,
  Star as StarIcon,
  EmojiEvents as TrophyIcon,
  Refresh as RefreshIcon,
  Upgrade as UpgradeIcon,
  History as HistoryIcon,
  CreditCard as CardIcon
} from '@mui/icons-material';
import subscriptionService, { SubscriptionStatus, PaymentHistory } from '../services/subscriptionService';
import SubscriptionPlans from '../components/SubscriptionPlans';

const PageContainer = styled(Box)`
  min-height: calc(100vh - 80px);
  background: linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%);
  padding: 2rem 0;
`;

const ModernCard = styled(Card)`
  background: white;
  border-radius: 16px;
  border: 1px solid #e2e8f0;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  transition: all 0.3s ease;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 30px rgba(0, 0, 0, 0.12);
  }
`;

const StatusChip = styled(Chip)<{ status: string }>`
  font-weight: bold;
  ${({ status, theme }) => {
    switch (status) {
      case 'active':
        return `
          background-color: ${alpha(theme.palette.success.main, 0.1)};
          color: ${theme.palette.success.main};
        `;
      case 'expired':
        return `
          background-color: ${alpha(theme.palette.error.main, 0.1)};
          color: ${theme.palette.error.main};
        `;
      default:
        return `
          background-color: ${alpha(theme.palette.grey[500], 0.1)};
          color: ${theme.palette.grey[700]};
        `;
    }
  }}
`;

const SubscriptionPage: React.FC = () => {
  const theme = useTheme();
  const [subscriptionStatus, setSubscriptionStatus] = useState<SubscriptionStatus | null>(null);
  const [paymentHistory, setPaymentHistory] = useState<PaymentHistory[]>([]);
  const [loading, setLoading] = useState(true);
  const [showSubscriptionPlans, setShowSubscriptionPlans] = useState(false);
  const [refreshing, setRefreshing] = useState(false);

  const fetchSubscriptionData = async () => {
    try {
      setRefreshing(true);
      const [statusData, historyData] = await Promise.all([
        subscriptionService.getSubscriptionStatus(),
        subscriptionService.getPaymentHistory()
      ]);
      setSubscriptionStatus(statusData);
      setPaymentHistory(historyData);
    } catch (error) {
      console.error('Error fetching subscription data:', error);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    fetchSubscriptionData();
  }, []);

  const handleSubscriptionChange = async () => {
    await fetchSubscriptionData();
    setShowSubscriptionPlans(false);
  };

  const getPlanIcon = (plan: string) => {
    switch (plan) {
      case 'monthly':
        return <StarIcon sx={{ color: '#3b82f6', fontSize: 24 }} />;
      case 'yearly':
        return <TrophyIcon sx={{ color: '#f59e0b', fontSize: 24 }} />;
      default:
        return <CheckIcon sx={{ color: '#10b981', fontSize: 24 }} />;
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-IN', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const getDaysRemaining = (endDate: string) => {
    const end = new Date(endDate);
    const now = new Date();
    const diffTime = end.getTime() - now.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return Math.max(0, diffDays);
  };

  const getProgressPercentage = (startDate: string, endDate: string) => {
    const start = new Date(startDate);
    const end = new Date(endDate);
    const now = new Date();
    
    const totalDuration = end.getTime() - start.getTime();
    const elapsed = now.getTime() - start.getTime();
    
    return Math.min(100, Math.max(0, (elapsed / totalDuration) * 100));
  };

  if (loading) {
    return (
      <PageContainer sx={{ px: { xs: 2, sm: 4, md: 6, lg: 8 } }}>
        <Container maxWidth="lg">
          <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
            <CircularProgress size={48} />
          </Box>
        </Container>
      </PageContainer>
    );
  }

  return (
    <PageContainer sx={{ px: { xs: 2, sm: 4, md: 6, lg: 8 } }}>
      <Container maxWidth="lg">
        {/* Header */}
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={4}>
          <Box>
            <Typography variant="h4" fontWeight="bold" gutterBottom>
              My Subscription
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Manage your subscription and view payment history
            </Typography>
          </Box>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={fetchSubscriptionData}
            disabled={refreshing}
            sx={{ borderRadius: 2 }}
          >
            {refreshing ? 'Refreshing...' : 'Refresh'}
          </Button>
        </Box>

        {/* Current Plan Card */}
        <ModernCard sx={{ mb: 4 }}>
          <CardContent sx={{ p: 4 }}>
            <Box display="flex" alignItems="center" justifyContent="space-between" mb={3}>
              <Box display="flex" alignItems="center" gap={2}>
                {getPlanIcon(subscriptionStatus?.plan || 'free')}
                <Box>
                  <Typography variant="h5" fontWeight="bold">
                    {subscriptionService.getPlanDisplayName(subscriptionStatus?.plan || 'free')}
                  </Typography>
                  <StatusChip 
                    label={subscriptionStatus?.isActive ? 'Active' : 'Inactive'}
                    status={subscriptionStatus?.isActive ? 'active' : 'inactive'}
                    size="small"
                  />
                </Box>
              </Box>
              
              {subscriptionStatus?.plan === 'free' && (
                <Button
                  variant="contained"
                  startIcon={<UpgradeIcon />}
                  onClick={() => setShowSubscriptionPlans(true)}
                  sx={{
                    background: 'linear-gradient(135deg, #667eea, #764ba2)',
                    borderRadius: 2,
                    px: 3
                  }}
                >
                  Upgrade Now
                </Button>
              )}
            </Box>

            {/* Plan Details */}
            <Box display="flex" gap={4} flexWrap="wrap">
              {subscriptionStatus?.plan !== 'free' && subscriptionStatus?.endDate && (
                <>
                  <Box>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      Valid Until
                    </Typography>
                    <Typography variant="h6" fontWeight="bold">
                      {formatDate(subscriptionStatus.endDate)}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {getDaysRemaining(subscriptionStatus.endDate)} days remaining
                    </Typography>
                  </Box>

                  {subscriptionStatus.startDate && (
                    <Box>
                      <Typography variant="body2" color="text.secondary" gutterBottom>
                        Started On
                      </Typography>
                      <Typography variant="h6" fontWeight="bold">
                        {formatDate(subscriptionStatus.startDate)}
                      </Typography>
                    </Box>
                  )}

                  {/* Progress Bar */}
                  {subscriptionStatus.startDate && (
                    <Box flex={1} minWidth={200}>
                      <Typography variant="body2" color="text.secondary" gutterBottom>
                        Subscription Progress
                      </Typography>
                      <LinearProgress
                        variant="determinate"
                        value={getProgressPercentage(subscriptionStatus.startDate, subscriptionStatus.endDate)}
                        sx={{
                          height: 8,
                          borderRadius: 4,
                          backgroundColor: alpha(theme.palette.primary.main, 0.1),
                          '& .MuiLinearProgress-bar': {
                            borderRadius: 4,
                            background: 'linear-gradient(90deg, #667eea, #764ba2)'
                          }
                        }}
                      />
                      <Typography variant="caption" color="text.secondary">
                        {Math.round(getProgressPercentage(subscriptionStatus.startDate, subscriptionStatus.endDate))}% completed
                      </Typography>
                    </Box>
                  )}
                </>
              )}
            </Box>

            {/* Non-auto-renewal notice for paid plans */}
            {subscriptionStatus?.plan !== 'free' && subscriptionStatus?.endDate && (
              <Box
                sx={{
                  mt: 3,
                  p: 2.5,
                  backgroundColor: alpha(theme.palette.warning.main, 0.1),
                  borderRadius: 2,
                  border: `1px solid ${alpha(theme.palette.warning.main, 0.3)}`,
                }}
              >
                <Typography variant="body1" color="warning.dark" sx={{ fontWeight: 600, mb: 1 }}>
                  ⚠️ Manual Renewal Required
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.6 }}>
                  This is a <strong>one-time payment subscription</strong> that will expire on{' '}
                  <strong>{formatDate(subscriptionStatus.endDate)}</strong>.{' '}
                  Your subscription will <strong>not auto-renew</strong> and you'll need to manually purchase a new plan after expiry.{' '}
                  You'll receive reminders before your subscription expires.
                </Typography>
              </Box>
            )}

            {/* Plan Features */}
            <Divider sx={{ my: 3 }} />
            <Typography variant="h6" fontWeight="bold" gutterBottom>
              Plan Features
            </Typography>
            <List dense>
              {subscriptionStatus?.plan === 'free' ? (
                <>
                  <ListItem sx={{ px: 0 }}>
                    <ListItemIcon sx={{ minWidth: 32 }}>
                      <CheckIcon color="success" fontSize="small" />
                    </ListItemIcon>
                    <ListItemText primary="1 free test per subject" />
                  </ListItem>
                  <ListItem sx={{ px: 0 }}>
                    <ListItemIcon sx={{ minWidth: 32 }}>
                      <CheckIcon color="success" fontSize="small" />
                    </ListItemIcon>
                    <ListItemText primary="Basic performance tracking" />
                  </ListItem>
                </>
              ) : (
                <>
                  <ListItem sx={{ px: 0 }}>
                    <ListItemIcon sx={{ minWidth: 32 }}>
                      <CheckIcon color="success" fontSize="small" />
                    </ListItemIcon>
                    <ListItemText primary="Unlimited tests in all subjects" />
                  </ListItem>
                  <ListItem sx={{ px: 0 }}>
                    <ListItemIcon sx={{ minWidth: 32 }}>
                      <CheckIcon color="success" fontSize="small" />
                    </ListItemIcon>
                    <ListItemText primary="Detailed performance analytics" />
                  </ListItem>
                  <ListItem sx={{ px: 0 }}>
                    <ListItemIcon sx={{ minWidth: 32 }}>
                      <CheckIcon color="success" fontSize="small" />
                    </ListItemIcon>
                    <ListItemText primary="Progress tracking & insights" />
                  </ListItem>
                  <ListItem sx={{ px: 0 }}>
                    <ListItemIcon sx={{ minWidth: 32 }}>
                      <CheckIcon color="success" fontSize="small" />
                    </ListItemIcon>
                    <ListItemText primary="Priority customer support" />
                  </ListItem>
                </>
              )}
            </List>
          </CardContent>
        </ModernCard>

        {/* Test Usage Overview */}
        {subscriptionStatus && (
          <ModernCard sx={{ mb: 4 }}>
            <CardContent sx={{ p: 4 }}>
              <Typography variant="h6" fontWeight="bold" gutterBottom>
                Test Usage Overview
              </Typography>
              <Box display="flex" gap={3} flexWrap="wrap">
                {Object.entries(subscriptionStatus.testLimits).map(([subject, limits]) => (
                  <Box key={subject} minWidth={150}>
                    <Typography variant="body2" color="text.secondary" gutterBottom sx={{ textTransform: 'capitalize' }}>
                      {subject}
                    </Typography>
                    <Box display="flex" alignItems="center" gap={1}>
                      <Typography variant="h6" fontWeight="bold">
                        {limits.remaining === 'unlimited' ? '∞' : limits.remaining}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {limits.remaining === 'unlimited' ? 'unlimited' : 'tests left'}
                      </Typography>
                    </Box>
                    {limits.remaining !== 'unlimited' && (
                      <LinearProgress
                        variant="determinate"
                        value={((limits.limit - limits.used) / limits.limit) * 100}
                        sx={{
                          mt: 1,
                          height: 4,
                          borderRadius: 2,
                          backgroundColor: alpha(theme.palette.primary.main, 0.1)
                        }}
                      />
                    )}
                  </Box>
                ))}
              </Box>
            </CardContent>
          </ModernCard>
        )}

        {/* Payment History */}
        <ModernCard>
          <CardContent sx={{ p: 4 }}>
            <Box display="flex" alignItems="center" gap={2} mb={3}>
              <HistoryIcon color="primary" />
              <Typography variant="h6" fontWeight="bold">
                Payment History
              </Typography>
            </Box>

            {paymentHistory.length === 0 ? (
              <Alert severity="info" sx={{ borderRadius: 2 }}>
                <Typography variant="body1">
                  No payment history found. {subscriptionStatus?.plan === 'free' && 'Upgrade to a paid plan to see your payment history here.'}
                </Typography>
              </Alert>
            ) : (
              <TableContainer component={Paper} sx={{ borderRadius: 2, border: `1px solid ${theme.palette.divider}` }}>
                <Table>
                  <TableHead>
                    <TableRow sx={{ bgcolor: alpha(theme.palette.primary.main, 0.05) }}>
                      <TableCell sx={{ fontWeight: 'bold' }}>Date</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>Amount</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>Status</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>Payment ID</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {paymentHistory.map((payment) => (
                      <TableRow key={payment.id} hover>
                        <TableCell>
                          <Box display="flex" alignItems="center" gap={1}>
                            <CardIcon fontSize="small" color="action" />
                            {formatDate(payment.date)}
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body1" fontWeight="bold">
                            {payment.formattedAmount}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <StatusChip 
                            label={payment.status}
                            status={payment.status}
                            size="small"
                          />
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" color="text.secondary" fontFamily="monospace">
                            {payment.id}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </CardContent>
        </ModernCard>

        {/* Subscription Plans Dialog */}
        <SubscriptionPlans
          open={showSubscriptionPlans}
          onClose={() => setShowSubscriptionPlans(false)}
          currentSubscription={subscriptionStatus || undefined}
          onSubscriptionChange={handleSubscriptionChange}
        />
      </Container>
    </PageContainer>
  );
};

export default SubscriptionPage; 